import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, Observable, of, Subject } from 'rxjs';
import { StaticContent, ToastType, UiTheme } from './ui-enum';
import { HttpClient } from '@angular/common/http';

export enum UIActionType{
  HideBackButton,
  ShowBackButton,
  ShowBookingSearchDialog,
}

export interface UIActionEvent{
  type: UIActionType,
  data?: any
}

interface ToastOption{
  type?: ToastType,
  duration?: number,
  summary?: string
}

export const TOAST_FOREVER = 999999;

@Injectable({
  providedIn: 'root'
})
export class UiService {

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private confirm: ConfirmationService,
    private http: HttpClient,
  ) { }

  actionsSubject = new Subject<UIActionEvent>();
  backSubject = new Subject<any>();
  stepperHeaderSubjet = new Subject<{
    steps: any,
    currentStep: number
  }>();
  private stepperHeaderClick = new Subject<number>();

  isShowingLoginDialog = new Subject<{
    show: boolean,
    register?: boolean
  }>();

  hideBackButton(){
    this.actionsSubject.next({
      type: UIActionType.HideBackButton
    })
  }

  showBackButton(){
    this.actionsSubject.next({
      type: UIActionType.ShowBackButton
    })
  }

  actions(): Observable<UIActionEvent>{
    return new Observable((observable) => {
      const sub = this.actionsSubject.subscribe((event) => {
        observable.next(event);
      })

      return {
        unsubscribe: () => {
          sub.unsubscribe();
        }
      }
    })
  }

  goBack(){
    this.backSubject.next(null);
  }

  back(): Observable<any>{
    return new Observable((observable) => {
      const sub = this.backSubject.subscribe((event) => {
        observable.next(event);
      })

      return {
        unsubscribe: () => {
          sub.unsubscribe();
        }
      }
    })
  }

  showLoginDialog(){
    this.isShowingLoginDialog.next({
      show: true,
      register: false
    });
  }

  showRegisterDialog(){
    this.isShowingLoginDialog.next({
      show: true,
      register: true
    })
  }

  hideLoginDialog(){
    this.isShowingLoginDialog.next({
      show: false,
    });
  }

  getHeaderStepper(): Subject<any>{
    return this.stepperHeaderSubjet;
  }

  showStepperOnHeader(steps: any, currentStep: number){
    this.stepperHeaderSubjet.next({
      steps: steps,
      currentStep: currentStep
    });
  }

  hideStepperOnHeader(){
    this.stepperHeaderSubjet.next(null);
  }

  onStepperHeaderClick(){
    return this.stepperHeaderClick;
  }

  emitStepperHeaderClick(index: number){
    this.stepperHeaderClick.next(index);
  }

  requestShowBookingSearchModal(){
    this.actionsSubject.next({
      type: UIActionType.ShowBookingSearchDialog
    });
  }

  toastSuccess(msg: string, opts?: ToastOption){
    this.toast(msg, {type: 'success', ...opts});
  }

  toastSuccessT(msg: string, opts?: ToastOption){
    this.toastT(msg, {type: 'success', ...opts});
  }

  toastErrorT(msg: string, opts?: ToastOption){
    this.toastT(msg, {type: 'error', ...opts});
  }

  toastError(msg: string, opts?: ToastOption){
    this.toast(msg, {type: 'error', ...opts});
  }

  toast(msg: string, opts?: ToastOption){
    const severity = opts?.type || 'success';
    const severitySummaryKey = `message-service-summary-${severity}`;
    const summary = opts && opts.summary ? opts.summary : this.translate.instant(severitySummaryKey); 
    if(summary != severitySummaryKey){
      this.messageService.add({severity: severity, summary: summary, detail: msg, key: 'toast', life: opts && opts.duration ? opts.duration : undefined});
      return;
    }

    this.translate.get(severitySummaryKey).subscribe(text => {
      this.messageService.add({severity: severity, summary: text, detail: msg, key: 'toast', life: opts && opts.duration ? opts.duration : undefined});
    })
  }

  toastT(msgKey: string, opts?: ToastOption){
    const value = this.translate.instant(msgKey);
    if(value && value !== msgKey){
      return this.toast(value, opts);
    }

    this.translate.get(msgKey).subscribe(text => {
      this.toast(text, opts);
    })
  }

  alert(message: string, title?: string, callback?: () => void){
    this.confirm.confirm({
      message: message,
      header: title,
      rejectVisible: false,
      key: 'global',
      acceptLabel: this.translate.instant('common.ok'),
      accept: () => {
        if(callback){
          callback();
        }
      }
    })
  }

  getThemeKey(theme: UiTheme){
    if(theme === 'BaseStack'){
      return 'bsl';
    } else if(theme === 'Xior'){
      return 'xior';
    } else if (theme === 'Triberal'){
      return 'triberal';
    } else {
      return '';
    }
  }

  loadStaticContent(content: StaticContent, theme: UiTheme) {
    const themeKey = this.getThemeKey(theme);

    //theme file path is [Filename]-[LanguageCode].[Theme].html (e.g. CookiePolicy-en.bsl.html)
    const filePath = `./assets/contents/${content}` + (themeKey && `.${themeKey}`)  + '.html';
    const fallbackPath = `./assets/contents/${content}.html`;

    return this.http.get(filePath, { responseType: 'text' }).pipe(
      catchError((error) => {
        console.warn(`File not found: ${filePath}, loading fallback...`);
        return this.http.get(fallbackPath, { responseType: 'text' }).pipe(
          catchError((fallbackError) => {
            console.error('Error loading fallback file:', fallbackError);
            return of('<p>Error loading content.</p>');
          })
        );
      })
    );
  }

}
